import { config } from '@gluestack-ui/config'
import { GluestackUIProvider, useToast } from '@gluestack-ui/themed'
import React, { ComponentType, FunctionComponent, useEffect, useState } from 'react'
import { AppState, Platform, StyleSheet, View } from 'react-native'
import FastImage from 'react-native-fast-image'
import RNBootSplash from 'react-native-bootsplash'
import { getTrackingStatus, requestTrackingPermission } from 'react-native-tracking-transparency'
import { useDispatch, useSelector } from 'react-redux'
import { LaunchLoadingScreen } from '../components/LaunchLoadingScreen'
import { NotificationPromptSheet } from '../components/NotificationPromptSheet'
import { BootAppFailed } from '../pages/BootAppFailed'
import { AuthenticationActions } from '../redux/authentication/actions'
import { apiTokenSelector } from '../redux/authentication/selectors'
import { HomeActions } from '../redux/home/actions'
import { loadingStatusSelector } from '../redux/loading/selectors'
import { LoaderName } from '../redux/loading/types'
import { MenuActions } from '../redux/menu/actions'
import { NotificationActions } from '../redux/notification/action'
import { notificationPromptedAtSelector, notificationsEnabledSelector } from '../redux/notification/selector'
import { RootState } from '../redux/reducers'
import { SplashScreenActions } from '../redux/splashScreen/actions'
import { splashScreenPendingUrlSelector, splashScreenUrlSelector } from '../redux/splashScreen/selector'
import { ThemeActions } from '../redux/theme/actions'
import { themeSelector } from '../redux/theme/selector'
import { isNotificationPromptDue, readNotificationPermission, startNotifications } from '../services/notification'
import { toastManager } from '../services/toast'

const MIN_LOADING_SCREEN_DURATION = 600
const BOOT_TIMEOUT = 20000

type LaunchStep = 'pending' | 'notification' | 'done'

const ToastInitializer = () => {
  const toast = useToast()

  useEffect(() => {
    toastManager.setToast(toast.show)
  }, [toast])

  return null
}

export const withBootApp = (Component: ComponentType): ComponentType => {
  const bootAppHOC: FunctionComponent<{}> = () => {
    const dispatch = useDispatch()
    const apiToken = useSelector(apiTokenSelector)
    const theme = useSelector(themeSelector)
    const signInloadingStatus = useSelector((state: RootState) => loadingStatusSelector(state, LoaderName.SIGN_IN_USER))
    const notificationsEnabled = useSelector(notificationsEnabledSelector)
    const notificationPromptedAt = useSelector(notificationPromptedAtSelector)
    const splashScreenUrl = useSelector(splashScreenUrlSelector)
    const splashScreenPendingUrl = useSelector(splashScreenPendingUrlSelector)
    const [launchImageUri] = useState(splashScreenUrl)
    const [minDelayElapsed, setMinDelayElapsed] = useState(false)
    const [loadingScreenHidden, setLoadingScreenHidden] = useState(false)
    const [launchStep, setLaunchStep] = useState<LaunchStep>('pending')
    const [bootAttempt, setBootAttempt] = useState(0)
    const [bootFailed, setBootFailed] = useState(false)
    const [trackingResolved, setTrackingResolved] = useState(false)
    const [permissionRead, setPermissionRead] = useState(false)
    const isAuthenticated = !!apiToken

    useEffect(() => {
      dispatch(AuthenticationActions.signInUser())
      requestTracking().finally(() => setTrackingResolved(true))
      readNotificationPermission().finally(() => setPermissionRead(true))
    }, [])

    useEffect(() => {
      if (!isAuthenticated || !notificationsEnabled) {
        return
      }
      startNotifications()
    }, [isAuthenticated, notificationsEnabled])

    useEffect(() => {
      if (!apiToken) {
        return
      }
      dispatch(ThemeActions.loadThemeConfig())
      dispatch(HomeActions.loadHomeConfig())
      dispatch(MenuActions.loadMenuConfig())
      dispatch(SplashScreenActions.loadSplashScreen())
    }, [apiToken])

    useEffect(() => {
      const timer = setTimeout(() => setMinDelayElapsed(true), MIN_LOADING_SCREEN_DURATION)
      return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
      const timer = setTimeout(() => setBootFailed(true), BOOT_TIMEOUT)
      return () => clearTimeout(timer)
    }, [bootAttempt])

    const appReady = !!theme && minDelayElapsed && (trackingResolved || bootFailed)

    useEffect(() => {
      if (!appReady || !permissionRead || launchStep !== 'pending') {
        return
      }
      setLaunchStep(isNotificationPromptDue(notificationsEnabled, notificationPromptedAt) ? 'notification' : 'done')
    }, [appReady, permissionRead, launchStep])

    const requestTracking = async () => {
      const trackingStatus = await getTrackingStatus()
      if (trackingStatus === 'not-determined') {
        await requestTrackingPermission()
      }
    }

    const retryBoot = () => {
      setBootAttempt(attempt => attempt + 1)
      dispatch(AuthenticationActions.signInUser())
      dispatch(ThemeActions.loadThemeConfig())
      dispatch(HomeActions.loadHomeConfig())
      dispatch(MenuActions.loadMenuConfig())
      dispatch(SplashScreenActions.loadSplashScreen())
    }

    const signInFailed = !!signInloadingStatus && !signInloadingStatus.status.ok
    const showBootFailed = (signInFailed || bootFailed) && !theme

    useEffect(() => {
      if (!showBootFailed) {
        return
      }
      let previousAppState = AppState.currentState
      const subscription = AppState.addEventListener('change', nextAppState => {
        if (/inactive|background/.test(previousAppState) && nextAppState === 'active') {
          retryBoot()
        }
        previousAppState = nextAppState
      })
      return () => subscription.remove()
    }, [showBootFailed])

    if (showBootFailed) {
      RNBootSplash.hide()
      return (
        <GluestackUIProvider config={config}>
          <BootAppFailed onRetry={retryBoot} />
        </GluestackUIProvider>
      )
    }

    const handleNotificationSheetClose = () => {
      dispatch(NotificationActions.storeNotificationPromptedAt(Date.now()))
      setLaunchStep('done')
    }

    if (Platform.OS === 'web') {
      if (!theme) {
        return null
      }
      return (
        <GluestackUIProvider config={config}>
          <ToastInitializer />
          <Component />
        </GluestackUIProvider>
      )
    }

    const shouldReveal = appReady && launchStep === 'done'

    return (
      <GluestackUIProvider config={config}>
        <View style={styles.container}>
          {appReady && (
            <>
              <ToastInitializer />
              <Component />
            </>
          )}
          {!loadingScreenHidden && (
            <LaunchLoadingScreen appReady={appReady} ready={shouldReveal} imageUri={launchImageUri} onFadeOutComplete={() => setLoadingScreenHidden(true)} />
          )}
          {!!splashScreenPendingUrl && (
            <FastImage
              source={{ uri: splashScreenPendingUrl, cache: FastImage.cacheControl.immutable }}
              style={styles.splashScreenPrefetch}
              onLoad={() => dispatch(SplashScreenActions.confirmSplashScreen())}
            />
          )}
          {appReady && <NotificationPromptSheet show={launchStep === 'notification'} onClose={handleNotificationSheetClose} />}
        </View>
      </GluestackUIProvider>
    )
  }

  return bootAppHOC
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  splashScreenPrefetch: {
    position: 'absolute',
    width: 1,
    height: 1,
    opacity: 0,
  },
})
